import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { MapContext } from "@trackcode/map";
import {
  isWencoDemoModeEnabled,
  isWencoMapCustomersEnabled,
  WENCO_DEMO_MODE_CHANGED
} from "common/wencoDemoMode";
import LayoutContext from "features/app/LayoutContext";
import { getWencoMapTerritories } from "features/shipment/WencoCustomersOnMap";
import { getDrivers } from "state/entities/members/selectors";
import { getEnhancedFilteredShipments } from "features/shipment/state/selectors";

const HUB_MARKER_SIZE_PX = 16;
const HUB_MARKER_RADIUS_PX = HUB_MARKER_SIZE_PX / 2;
const HUB_MARKER_SIZE_ACTIVE_PX = 20;
const HUB_MARKER_RADIUS_ACTIVE_PX = HUB_MARKER_SIZE_ACTIVE_PX / 2;

function buildHubOverlayElement(
  color,
  memberLabel,
  { isActive = false, onClick = null } = {}
) {
  const root = document.createElement("div");
  root.style.cssText = [
    "position:absolute",
    onClick ? "pointer-events:auto;cursor:pointer" : "pointer-events:none"
  ].join(";");

  root.title = memberLabel
    ? `Wohnort Mitarbeiter ${memberLabel}`
    : "Wohnort Mitarbeiter";

  const markerSize = isActive ? HUB_MARKER_SIZE_ACTIVE_PX : HUB_MARKER_SIZE_PX;

  const hubMarker = document.createElement("span");
  hubMarker.style.cssText = [
    "display:block",
    "box-sizing:border-box",
    `width:${markerSize}px`,
    `height:${markerSize}px`,
    `background:${color}`,
    isActive ? "border:2px solid #2a6fa8" : "border:2px solid #ffffff",
    isActive
      ? "box-shadow:0 0 0 2px rgba(42,111,168,0.35),0 2px 6px rgba(0,0,0,0.25)"
      : "box-shadow:0 1px 4px rgba(0,0,0,0.22)"
  ].join(";");
  root.appendChild(hubMarker);

  if (memberLabel) {
    const label = document.createElement("span");
    label.textContent = memberLabel;
    label.style.cssText = [
      "position:absolute",
      `left:${markerSize + 6}px`,
      "top:50%",
      "transform:translateY(-50%)",
      "padding:2px 6px",
      "border-radius:4px",
      isActive
        ? "background:#eef5fa;border:1px solid #2a6fa8;color:#1a4a6e"
        : "background:rgba(255,255,255,0.94);border:1px solid #e2e0da;color:#1a1a1a",
      "font-size:11px",
      "font-weight:700",
      "line-height:1.2",
      "white-space:nowrap",
      isActive
        ? "box-shadow:0 0 0 2px rgba(42,111,168,0.2)"
        : "box-shadow:0 1px 2px rgba(0,0,0,0.08)"
    ].join(";");
    root.appendChild(label);
  }

  if (onClick) {
    root.addEventListener("click", event => {
      event.stopPropagation();
      onClick();
    });
  }

  return root;
}

function buildCustomerOverlayElement(
  color,
  customerKey,
  { isActive = false, onClick = null } = {}
) {
  const root = document.createElement("div");
  root.style.cssText = [
    "position:absolute",
    "transform:translate(-50%,-50%)",
    onClick ? "pointer-events:auto;cursor:pointer" : "pointer-events:none"
  ].join(";");

  if (customerKey) {
    root.title = `Kunde ${customerKey}`;
  }

  const dotSize = isActive ? 14 : 10;

  const dot = document.createElement("span");
  dot.style.cssText = [
    "display:block",
    `width:${dotSize}px`,
    `height:${dotSize}px`,
    "border-radius:50%",
    `background:${color}`,
    isActive ? "border:2px solid #2a6fa8" : "border:1.5px solid #ffffff",
    isActive
      ? "box-shadow:0 0 0 2px rgba(42,111,168,0.35)"
      : "box-shadow:0 1px 2px rgba(0,0,0,0.15)"
  ].join(";");
  root.appendChild(dot);

  if (onClick) {
    root.addEventListener("click", event => {
      event.stopPropagation();
      onClick();
    });
  }

  return root;
}

function createHtmlOverlay(
  position,
  elementFactory,
  { anchor = "center", activeHub = false } = {}
) {
  class HtmlOverlay extends window.google.maps.OverlayView {
    onAdd() {
      this.div = elementFactory();
      this.getPanes().floatPane.appendChild(this.div);
    }

    draw() {
      if (!this.div) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(
        new window.google.maps.LatLng(position.lat, position.lng)
      );
      if (!point) return;
      if (anchor === "hub") {
        const radius = activeHub
          ? HUB_MARKER_RADIUS_ACTIVE_PX
          : HUB_MARKER_RADIUS_PX;
        this.div.style.left = `${point.x - radius}px`;
        this.div.style.top = `${point.y - radius}px`;
        this.div.style.transform = "none";
      } else {
        this.div.style.left = `${point.x}px`;
        this.div.style.top = `${point.y}px`;
        this.div.style.transform = "translate(-50%, -50%)";
      }
    }

    onRemove() {
      if (this.div) {
        this.div.remove();
        this.div = null;
      }
    }
  }

  return new HtmlOverlay();
}

const WencoCustomerMapPinLayer = ({ shipments, drivers }) => {
  const map = useContext(MapContext);
  const layoutContext = useContext(LayoutContext) || {};
  const {
    viewMode,
    onMapDriverClick,
    activeMapMemberIds
  } = layoutContext;
  const activeMemberIdSet = useMemo(
    () => new Set((activeMapMemberIds || []).map(id => Number(id))),
    [activeMapMemberIds]
  );
  const hubOverlaysRef = useRef([]);
  const customerOverlaysRef = useRef([]);
  const polylinesRef = useRef([]);
  const [demoEnabled, setDemoEnabled] = useState(isWencoDemoModeEnabled());

  const mapLinksEnabled =
    demoEnabled &&
    viewMode === "workbook" &&
    typeof onMapDriverClick === "function";

  useEffect(() => {
    const onDemoChange = () => setDemoEnabled(isWencoDemoModeEnabled());
    window.addEventListener(WENCO_DEMO_MODE_CHANGED, onDemoChange);
    return () =>
      window.removeEventListener(WENCO_DEMO_MODE_CHANGED, onDemoChange);
  }, []);

  const territories = useMemo(
    () => getWencoMapTerritories(shipments, drivers),
    [shipments, drivers]
  );

  const territoriesKey = useMemo(
    () =>
      [
        territories.hubs
          .map(
            hub =>
              `${hub.key}:${hub.position.lat},${hub.position.lng}:${hub.memberLabel}`
          )
          .join("|"),
        territories.customers
          .map(
            customer =>
              `${customer.key}:${customer.position.lat},${customer.position.lng}`
          )
          .join("|"),
        territories.spokes.length,
        (activeMapMemberIds || []).join(","),
        mapLinksEnabled ? "links" : "static"
      ].join("||"),
    [territories, activeMapMemberIds, mapLinksEnabled]
  );

  useEffect(() => {
    hubOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    customerOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    polylinesRef.current.forEach(polyline => polyline.setMap(null));
    hubOverlaysRef.current = [];
    customerOverlaysRef.current = [];
    polylinesRef.current = [];

    if (
      !map ||
      !demoEnabled ||
      !isWencoMapCustomersEnabled() ||
      !window.google?.maps ||
      territories.hubs.length === 0
    ) {
      return undefined;
    }

    territories.spokes.forEach(spoke => {
      const polyline = new window.google.maps.Polyline({
        path: spoke.path.map(
          point => new window.google.maps.LatLng(point.lat, point.lng)
        ),
        strokeColor: spoke.color,
        strokeOpacity: 0.5,
        strokeWeight: 1.5,
        geodesic: false,
        map
      });
      polylinesRef.current.push(polyline);
    });

    territories.hubs.forEach(hub => {
      const memberId = Number(hub.memberId);
      const isActive =
        Number.isFinite(memberId) && activeMemberIdSet.has(memberId);
      const overlay = createHtmlOverlay(
        hub.position,
        () =>
          buildHubOverlayElement(hub.color, hub.memberLabel, {
            isActive,
            onClick: mapLinksEnabled
              ? () => onMapDriverClick(memberId)
              : null
          }),
        { anchor: "hub", activeHub: isActive }
      );
      overlay.setMap(map);
      hubOverlaysRef.current.push(overlay);
    });

    territories.customers.forEach(customer => {
      const memberId = Number(customer.memberId);
      const customerKey = String(customer.key).replace(/^wenco-customer-/, "");
      const isActive =
        Number.isFinite(memberId) && activeMemberIdSet.has(memberId);
      const overlay = createHtmlOverlay(customer.position, () =>
        buildCustomerOverlayElement(customer.color, customerKey, {
          isActive,
          onClick: mapLinksEnabled
            ? () => onMapDriverClick(memberId)
            : null
        })
      );
      overlay.setMap(map);
      customerOverlaysRef.current.push(overlay);
    });

    return () => {
      hubOverlaysRef.current.forEach(overlay => overlay.setMap(null));
      customerOverlaysRef.current.forEach(overlay => overlay.setMap(null));
      polylinesRef.current.forEach(polyline => polyline.setMap(null));
      hubOverlaysRef.current = [];
      customerOverlaysRef.current = [];
      polylinesRef.current = [];
    };
  }, [
    map,
    demoEnabled,
    territoriesKey,
    territories,
    activeMapMemberIds,
    activeMemberIdSet,
    mapLinksEnabled,
    onMapDriverClick
  ]);

  return null;
};

WencoCustomerMapPinLayer.propTypes = {
  shipments: PropTypes.shape({}),
  drivers: PropTypes.shape({})
};

WencoCustomerMapPinLayer.defaultProps = {
  shipments: {},
  drivers: {}
};

const mapStateToProps = state => ({
  shipments: getEnhancedFilteredShipments(state),
  drivers: getDrivers(state)
});

export default connect(mapStateToProps)(WencoCustomerMapPinLayer);
